import {
  Component,
  AfterViewInit,
  ViewChild,
  NgZone,
  ElementRef,
} from "@angular/core";
import {
  UntypedFormControl,
  Validators,
  UntypedFormGroup,
} from "@angular/forms";
import { Observable, of as observableOf, BehaviorSubject } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";

import {
  TemplateCategory,
  TemplateHead,
  CheckTopic,
  TemplateAudit,
  TemplateTopic,
} from "../model/TemplateTopics";
import { AuditContent } from "../model/TemplateRulebook";
import { TemplateService } from "../service/template.service";
import { AlertifyService } from "../../_models/alertify.service";
import { HelpInfoService } from "../../_common/help-info.service";

import { NewEvent } from "../../graph/new-event";
import { GraphService } from "../../graph/graph.service";
import { AzureAuthService } from "../../azure-auth/azure-auth.service";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

@Component({
  selector: "app-new-audit",
  templateUrl: "./new-audit.component.html",
  styleUrls: ["./new-audit.component.css"],
  standalone: false,
})
export class NewAuditComponent implements AfterViewInit {
  @ViewChild("mapContainer") mapElement: any;

  templateHead = new UntypedFormGroup({
    auditName: new UntypedFormControl("", [Validators.required]),
    startDate: new UntypedFormControl(new Date()),
    endDate: new UntypedFormControl(new Date()),
    shared: new UntypedFormControl(""),
    facilityName: new UntypedFormControl(""),
    facilityAddress: new UntypedFormControl(""),
  });

  tHead: TemplateHead | undefined;
  permission = "Not";
  dualLanguageCheckbox = false; // Dual language checkbox, default is false.

  step = -1;
  csskey?: number;
  isShared?: boolean;
  isSavable = false;

  data$: Observable<TemplateAudit>;
  topics: TemplateCategory[];
  currentCategory: string | undefined;
  cacheTopickey: number[] = new Array();
  cacheCustomTopic: number[] = new Array();

  isLoading = false; // Flag to froze the page when loading.
  map: google.maps.Map;

  address: Object;
  establishmentAddress: Object;
  startDateValue: Date;
  formattedAddress: string;
  formattedEstablishmentAddress: string;
  editable = true;

  constructor(
    public service: TemplateService,
    private router: Router,
    private route: ActivatedRoute,
    private alertifyService: AlertifyService,
    public zone: NgZone,
    private azureService: AzureAuthService,
    private graphService: GraphService,
    public dialog: MatDialog,
    private helpInfoService: HelpInfoService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(
      (paraMap) => (this.csskey = +paraMap.get("id")),
    );

    this.data$ = this.service.getAcronyms(this.csskey);
    this.startDateValue = new Date();
    this.refreshTopic();
    if (this.csskey && this.csskey > 0) {
      this.service.touchTemplate(this.csskey.toString());
      this.service.updateTemplateDateStorage(this.csskey ?? 0);
    }
  }

  ngAfterViewInit() {
    this.initMat();
  }

  initMat(): void {
    const myLatlng = { lat: 49.266865, lng: -123.111395 };
    const coordinates = new google.maps.LatLng(myLatlng);

    const marker = new google.maps.Marker({
      position: coordinates,
      map: this.map,
      title: "STP",
    });
    this.mapInitializer(coordinates, marker);
  }

  mapInitializer(coordinates: google.maps.LatLng, marker: google.maps.Marker) {
    const mapOptions: google.maps.MapOptions = {
      center: coordinates,
      zoom: 8,
    };
    this.map = new google.maps.Map(this.mapElement.nativeElement, mapOptions);
    this.map.setCenter(coordinates);
    this.map.setMapTypeId("roadmap");
    marker.setMap(this.map);

    //this.getAddress(coordinates);
    this.map.addListener("click", (event) => {
      this.placeMapMarker(event.latLng);
    });
  }

  refreshTopic() {
    this.service.showLoadingAnimation(true);
    this.data$.subscribe(
      (res) => {
        this.fillTopic(res);
        const accountStr = localStorage.getItem("account");
        let userId = accountStr ? JSON.parse(accountStr).id : null;
        if (res.auditHead && res.auditHead.clientId !== userId) {
          if (res.auditHead.permission.trim() === "View") {
            this.editable = false;
          }
        }
        this.service.showLoadingAnimation(false);
      },
      (error) => {
        this.alertifyService.error(error.error);
        this.service.showLoadingAnimation(false);
      },
    );
  }

  fillTopic(data: TemplateAudit) {
    for (var ca of data.categoryAcronyms) {
      if (ca.acronyms.find((a) => a.selected > 0)) {
        this.currentCategory = ca.category;
        break;
      }
    }
    if (this.currentCategory === undefined) {
      this.currentCategory = data.categoryAcronyms[0].category;
    }

    if (data.auditHead != undefined) {
      this.fillTemplateHead(data.auditHead);
      this.permission =
        data.auditHead.permission !== null
          ? data.auditHead.permission.trim()
          : "View";
      this.isShared = data.auditHead.shared;
      this.dualLanguageCheckbox = data.auditHead.dual ?? false;
    }

    this.topics = data.categoryAcronyms;
    this.topics.forEach((acronym) => {
      acronym.selected = 0;

      acronym.acronyms.forEach((a) => {
        acronym.selected += a.selected;
        if (acronym.category == "IP" || acronym.category == "CAN-MEX") {
          a.title = a.title.substring(a.title.indexOf("- ") + 1);
        }
      });
    });
  }

  getAddress(place: object) {
    this.address = place["formatted_address"];
    this.formattedAddress = place["formatted_address"];
    this.refreshAddressOnMap();

    //this.zone.run(() => (this.formattedAddress = place["formatted_address"]));
  }

  getEstablishmentAddress(place: object) {
    this.establishmentAddress = place["formatted_address"];

    this.formattedEstablishmentAddress = place["formatted_address"];
    this.zone.run(() => {
      this.formattedEstablishmentAddress = place["formatted_address"];
    });
  }

  getAddrComponent(place, componentTemplate) {
    let result;

    for (let i = 0; i < place.address_components.length; i++) {
      const addressType = place.address_components[i].types[0];
      if (componentTemplate[addressType]) {
        result = place.address_components[i][componentTemplate[addressType]];
        return result;
      }
    }
    return;
  }

  getStreetNumber(place) {
    const COMPONENT_TEMPLATE = { street_number: "short_name" },
      streetNumber = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return streetNumber;
  }

  getStreet(place) {
    const COMPONENT_TEMPLATE = { route: "long_name" },
      street = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return street;
  }

  getCity(place) {
    const COMPONENT_TEMPLATE = { locality: "long_name" },
      city = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return city;
  }

  getState(place) {
    const COMPONENT_TEMPLATE = { administrative_area_level_1: "short_name" },
      state = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return state;
  }

  getDistrict(place) {
    const COMPONENT_TEMPLATE = { administrative_area_level_2: "short_name" },
      state = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return state;
  }

  getCountryShort(place) {
    const COMPONENT_TEMPLATE = { country: "short_name" },
      countryShort = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return countryShort;
  }

  getCountry(place) {
    const COMPONENT_TEMPLATE = { country: "long_name" },
      country = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return country;
  }

  getPostCode(place) {
    const COMPONENT_TEMPLATE = { postal_code: "long_name" },
      postCode = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    return postCode;
  }

  // Fill value for Inputs in Reactive Form with data from backend.
  fillTemplateHead(headData: TemplateHead) {
    this.tHead = headData;
    const auditNameControl = this.templateHead.get("auditName");
    if (auditNameControl) {
      auditNameControl.setValue(this.tHead.auditName);
    }
    const startDateControl = this.templateHead.get("startDate");
    if (startDateControl) {
      startDateControl.setValue(this.tHead.auditStart);
    }
    const endDateControl = this.templateHead.get("endDate");
    if (endDateControl) {
      endDateControl.setValue(this.tHead.auditEnd);
    }
    const facilityNameControl = this.templateHead.get("facilityName");
    if (facilityNameControl) {
      facilityNameControl.setValue(this.tHead.facilityName);
    }
    const facilityAddressControl = this.templateHead.get("facilityAddress");
    if (facilityAddressControl) {
      facilityAddressControl.setValue(this.tHead.facilityLocation);
    }

    if (headData.facilityLocation) {
      this.formattedAddress = headData.facilityLocation;
      this.refreshAddressOnMap();
    }
  }

  mapCategory(category: string): string {
    switch (category) {
      case "ENV":
        return "Environment";
      case "IP":
        return "International";
      case "OSHA":
        return "Health & Safety";
      case "TRANSPORTATION":
        return "Transportation";
      case "CAN-MEX":
        return "CAN/MEX";
      default:
        return category;
    }
  }

  getErrorMessage() {
    const auditNameControl = this.templateHead.get("auditName");
    return auditNameControl && auditNameControl.hasError("required")
      ? "Please enter an audit name before saving template."
      : "";
  }

  onCategoryChange(category: string) {
    this.currentCategory = category;
    this.step = -1;
  }

  /**
   * When a start date keyed in, set the default value and min value for the end date Input.
   * @param input start date
   */
  OnStartDateChange(input: Date) {
    this.startDateValue = input;
    this.startDateValue.setHours(0, 0, 0, 0);
    if (input > this.templateHead.get("endDate").value) {
      this.templateHead.get("endDate").setValue(new Date(input));
    }
    this.isSavable = true;
  }

  onEndDateChange(input: Date) {
    this.isSavable = true;
  }

  async addEventToCalendar(): Promise<void> {
    var model = new NewEvent();
    const timeZone = this.azureService.user?.timeZone;
    const graphEvent = model.getGraphEvent(timeZone);

    try {
      await this.graphService.addEventToCalendar(graphEvent);
      this.alertifyService.success("Event created.");
    } catch (error) {
      this.alertifyService.error("Error creating event: " + error.message);
    }
  }

  async onAddEventToCalendar(): Promise<void> {
    let model = new NewEvent();
    model.subject = "Audit: " + this.templateHead.get("auditName").value;
    var start = new Date(this.templateHead.get("startDate").value);
    var today = new Date();
    start.setHours(today.getHours(), today.getMinutes(), today.getSeconds(), 0);
    model.start = start.toLocaleString();
    var end = new Date(this.templateHead.get("endDate").value);
    end.setHours(23, 59, 59, 0);

    model.end = end.toLocaleString();
    //console.log("end: " + model.end);
    model.body =
      "Facility: " +
      this.templateHead.get("facilityName").value +
      " at " +
      this.templateHead.get("facilityAddress").value;

    const timeZone =
      (this.azureService.user && this.azureService.user.timeZone) || "UTC";
    const graphEvent = model.getGraphEvent(timeZone);

    try {
      await this.signIn();
      if (this.graphService.isLogin()) {
        await this.graphService.addEventToCalendar(graphEvent);
        this.alertifyService.success("Event created on Outlook calendar.");
      }
    } catch (error) {
      this.alertifyService.error("Error creating event:" + error.message);
    }
  }

  // sign in microsoft account.
  async signIn(): Promise<void> {
    await this.azureService.signIn();
  }

  // Click publication accordion to expand it and show topic list
  setStep(index: number, acronym: string) {
    this.step = index;
    this.getTopics(acronym);
    let pairedAcronym = this.getDualLanguageAcronym(acronym);

    // If the acronym is dual language, then get the topics for the paired acronym because we need to select the mirrored topic too when a topic is selected.
    if (pairedAcronym !== "") {
      this.getTopics(pairedAcronym);
    }
  }

  /**
   * Get the dual language acronym for the given acronym.
   * @param acronym {string} - the acronym of the publication.
   * @return {string} - the dual language acronym, or empty string if not dual language.
   * @example
   * CHN -> CHNL; CHNL -> CHN
   * DEUE -> DEUL; DEUL -> DEUE the only exception that does not follow the L suffix rule.
   *  */
  getDualLanguageAcronym(acronym: string): string {
    if (this.currentCategory !== "IP" && this.currentCategory !== "CAN-MEX") {
      return "";
    }
    if (acronym == "DEUE") return "DEUL";
    if (acronym == "DEUL") return "DEUE";
    let pairedAcronym = this.topics.find((x) =>
      x.acronyms.some((a) => a.acronym === acronym + "L"),
    );
    if (pairedAcronym) {
      return acronym + "L";
    } else {
      if (acronym.endsWith("L")) {
        pairedAcronym = this.topics.find((x) =>
          x.acronyms.some((a) => a.acronym === acronym.replace(/L$/, "")),
        );
        if (pairedAcronym) {
          return acronym.replace(/L$/, "");
        }
      }
      return "";
    }
  }

  /**
   *  Call bankend to get the topics for the publication.
   *  If the topics are already loaded, then do nothing.
   */
  getTopics(acronym: string) {
    if (!acronym || 0 === acronym.length) {
      this.alertifyService.warning("Invalid acronym!");
      return false;
    }

    let theAcronym = this.topics.find((a) =>
      a.acronyms.some((item) => item.acronym === acronym),
    );
    if (!theAcronym) return;

    let theTopics = theAcronym.acronyms.find((a) => a.acronym === acronym);
    if (!theTopics) return;

    if (theTopics.topics == null) {
      this.service
        .getTopics(
          acronym,
          theTopics.releaseNum,
          this.csskey ?? 0,
          theTopics.selected,
        )
        .subscribe(
          (res: TemplateTopic[]) => {
            theTopics.topics = res;
            theTopics.topics.forEach((tp) => {
              tp!.topic = this.service.decodeHtml(tp!.topic);
            });
          },
          (err) => {
            if (err.status === "404") {
              this.alertifyService.warning(
                "Cannot find the protocol or not authorized.",
              );
            } else {
              this.alertifyService.error(err.error);
            }
          },
        );
    }
  }

  unsetStep() {
    this.step = -1;
  }

  onPanelClosed(index: number) {
    if (this.step === index) {
      this.step = -1;
    }
  }

  // Check if the template has a dual language protocol.
  hasDualLanguageProtocol(): boolean {
    var acronyms = this.topics
      .flatMap((ca) => ca.acronyms)
      .filter((x) => x.selected > 0);
    if (acronyms.length === 0) {
      return false;
    }

    for (let element of acronyms) {
      let thePairedAcronym = this.getDualLanguageAcronym(element.acronym);
      if (thePairedAcronym) {
        let pairedAcronymObj = acronyms.find(
          (x) => x.acronym === thePairedAcronym,
        );
        if (
          pairedAcronymObj &&
          pairedAcronymObj.releaseNum === element.releaseNum
        ) {
          return true;
        }
      }
    }
    return false;
  }

  // Get the release number of the protocol.
  getReleaseNumber(acronym: string): number {
    const found = this.topics
      .flatMap((x) => x.acronyms)
      .find((a) => a.acronym === acronym);
    return found ? found.releaseNum : -1;
  }

  // Dual language checkbox change event.
  onDualChange(event) {
    this.dualLanguageCheckbox = event.checked;
    this.isSavable = true;
    if (!this.dualLanguageCheckbox) {
      this.service.syncDualLanguageTemplate(this.csskey ?? 0, false).subscribe(
        // call backend to set the dual language setting to false.
        (res) => {
          if (res >= 0) {
            if (this.csskey && this.csskey > 0) {
              this.service.setDualLanguageTemplate(0); // Set the dual language setting for other operations.
              this.service.updateTemplateDualLanguage(this.csskey ?? 0, 0); // Update the session cache.
            }
            //this.data$ = this.service.getAcronyms(this.csskey);
          }
        },
        (err) => {
          this.alertifyService.error(err.error);
        },
      );
      return;
    }
    // If dual language is checked, then set the mirrored topics.
    if (this.csskey && this.csskey > 0) {
      this.service.updateTemplateDualLanguage(
        this.csskey ?? 0,
        this.hasDualLanguageProtocol() ? 2 : 1,
      ); // Update the session cache.
      this.service.setDualLanguageTemplate(
        this.hasDualLanguageProtocol() ? 2 : 1,
      ); // Set the dual language setting for other operations.
      this.service
        .syncDualLanguageTemplate(this.csskey ?? 0, this.dualLanguageCheckbox)
        .subscribe(
          (res) => {
            if (res > 0) {
              this.data$ = this.service.getAcronyms(this.csskey);
              this.refreshTopic(); // Refresh the topic list to get the mirrored topics.
              this.service.removeTemplateFromStorage(this.csskey ?? 0); // Remove the cache to get the latest data.
            }
          },
          (err) => {
            this.alertifyService.error(err.error);
          },
        ); // Call backend to set the dual language setting to true.
    } else {
      // Mirror the topics in the template.
      this.topics
        .filter(
          (x) =>
            (x.category === "IP" || x.category === "CAN-MEX") && x.selected > 0,
        )
        .forEach((element) => {
          element.acronyms
            .filter((acronym) => acronym.selected > 0)
            .forEach((acronym) => {
              const mirroredAcronym = this.getDualLanguageAcronym(
                acronym.acronym,
              );
              if (mirroredAcronym) {
                if (
                  this.getReleaseNumber(mirroredAcronym) === acronym.releaseNum
                ) {
                  acronym.topics
                    .filter((tp) => tp.selected)
                    .forEach((tp) =>
                      this.setMirroredTopic(mirroredAcronym, tp),
                    );
                }
              }
            });
        });
    }
  }

  /**
   * Set the topic to be checked in the protocol.
   * @param mirroredAcronym {string} MEX - the acronym of the protocol to be checked.
   * @param mirroredTopic {TemplateTopic} - the topic of the mirrored protocol.
   */
  setMirroredTopic(mirroredAcronym: string, mirroredTopic: TemplateTopic) {
    const category = this.topics.find((ca) =>
      ca.acronyms.some((ac) => ac.acronym === mirroredAcronym),
    );
    if (!category) return;

    const acronymObj = category.acronyms.find(
      (ac) => ac.acronym === mirroredAcronym,
    );
    if (!acronymObj) return;

    const topicObj = acronymObj.topics.find(
      (tp) => tp.topic === mirroredTopic.topic,
    );
    if (topicObj && !topicObj.selected) {
      topicObj.selected = true;
      this.onTopicChange(topicObj, acronymObj.title, acronymObj.acronym);
    }
  }

  /**
   * Get the mirrored topic key for the topic.
   * @param topic {string} - the topic of the publication.
   * @param acronym {string} - the acronym of publication.
   * @param releaseNum {number} - the release number of the publication, optional.
   * @returns {number} - the topic key of the mirrored topic in the mirrored publication, or -1 if not found.
   */
  getMirroredTopicKey(
    topic: string,
    acronym: string,
    releaseNum?: number,
  ): number {
    if (!this.dualLanguageCheckbox || !topic || !acronym) {
      return -1;
    }
    let pairedAcronym = this.getDualLanguageAcronym(acronym);

    if (pairedAcronym !== "") {
      for (const ca of this.topics) {
        if (ca.category !== this.currentCategory) continue;
        const ac = ca.acronyms.find(
          (ac) => ac.acronym === pairedAcronym && ac.releaseNum === releaseNum,
        );
        const tp = ac?.topics.find((tp) => tp.topic === topic);
        if (tp) return tp.topicKey;
      }
    }
    return -1; // not found.
  }

  isAuditNameValid(): boolean {
    const auditNameControl = this.templateHead.get("auditName");
    return (
      auditNameControl !== null &&
      auditNameControl.valid &&
      auditNameControl.value.trim() !== ""
    );
  }

  onSaveTemplate() {
    let errorMessage = this.getErrorMessage();
    if (errorMessage.length > 0) {
      this.alertifyService.error(errorMessage);
      return false;
    }
    this.service.showLoadingAnimation(true);
    this.isLoading = true;
    const accountStr = localStorage.getItem("account");
    let userId = accountStr ? JSON.parse(accountStr).id : null;
    var th: TemplateHead = {
      csskey: this.csskey ?? 0,
      auditName: this.templateHead.get("auditName")?.value.trim(),
      facilityName: this.templateHead.get("facilityName")?.value,
      auditStart: this.templateHead.get("startDate")?.value,
      auditEnd: this.templateHead.get("endDate")?.value,
      facilityLocation: this.formattedAddress ?? "",
      shared: this.permission != "Not",
      permission: this.permission,
      clientId: userId,
      topicKey: this.cacheTopickey,
      dual: this.dualLanguageCheckbox,
      customTopic: this.cacheCustomTopic,
    };

    this.service.createTemplate(th).subscribe(
      (value) => {
        this.isLoading = false;
        this.isSavable = false;
        this.service.setAuditName(
          th.auditName,
          th.facilityLocation ?? "",
          new Date(th.auditStart).toDateString() +
            " - " +
            new Date(th.auditEnd).toDateString(),
          "",
        );
        this.service.setDualLanguageTemplate(
          this.dualLanguageCheckbox
            ? this.hasDualLanguageProtocol()
              ? 2
              : 1
            : 0,
        );

        var theItem = this.service.getTemplateItemFromCache(value.csskey);
        if (theItem) {
          theItem.auditName = th.auditName;
          theItem.cssDate = new Date();
          theItem.dualLanguage = this.dualLanguageCheckbox
            ? this.hasDualLanguageProtocol()
              ? 2
              : 1
            : 0;
          theItem.facilityLocation = th.facilityLocation ?? "";
          theItem.startDate = th.auditStart;
          theItem.endDate = th.auditEnd;
          if (theItem.sharedBy === "self") {
            theItem.isShared = this.isShared ? this.isShared : false;
            theItem.permission = this.permission;
          }
        } else {
          this.csskey = value.csskey;
          theItem = {
            csskey: value.csskey,
            auditName: th.auditName,
            cssDate: new Date(),
            isShared: this.isShared ? this.isShared : false,
            sharedBy: "self",
            permission: this.permission,
            status: "",
            citationLink: "",
            isUpdateAvailable: false,
            dualLanguage: this.dualLanguageCheckbox
              ? this.hasDualLanguageProtocol()
                ? 2
                : 1
              : 0,
            color: "",
            xceleratorLink: "",
            downloadable: false,
            isBuilding: false,
            monitoring: "",
            percentComplete: "",
            facilityLocation: th.facilityLocation ?? "",
            startDate: th.auditStart,
            endDate: th.auditEnd,
          };
        }

        this.service.updateItemInCache(theItem);
        this.alertifyService.success("Audit template saved.");
      },
      (error) => {
        this.alertifyService.error(error.error);
        this.service.showLoadingAnimation(false);
        this.isLoading = false;
      },
      () => {
        this.service.showLoadingAnimation(false);
        this.isLoading = false;
      },
    );
  }

  // Place a marker on map.
  placeMapMarker(loc) {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: loc }, (results, status) => {
      if (status === "OK") {
        if (results[0]) {
          const facilityAddressControl =
            this.templateHead.get("facilityAddress");
          if (facilityAddressControl) {
            facilityAddressControl.setValue(results[0].formatted_address);
          }

          let marker = new google.maps.Marker({
            map: this.map,
            position: loc,
          });
          let coordinates = loc;
          this.mapInitializer(coordinates, marker);
        } else {
          alert("No results found");
        }
      } else {
        alert("Geocoder failed due to: " + status);
      }
    });
  }

  backToHome() {
    this.router.navigateByUrl("/audithub/apptool/list");
  }

  // event handler for user inputs an address.
  refreshAddressOnMap() {
    const geocoder = new google.maps.Geocoder();
    if (
      this.formattedAddress == null ||
      this.formattedAddress.toString().trim().length == 0
    )
      geocoder.geocode(
        { address: this.formattedAddress },
        (results, status) => {
          if (status === "OK") {
            if (results && results[0]) {
              let coordinates = results[0].geometry.location;
              this.map.setCenter(coordinates);
              let marker = new google.maps.Marker({
                map: this.map,
                position: coordinates,
              });
              this.mapInitializer(coordinates, marker);
            }
          } else {
            alert(
              "Geocode was not successful for the following reason: " +
                status +
                " for address: " +
                this.formattedAddress,
            );
          }
        },
      );
  }

  // address lookup
  addressLookup(coordinates: google.maps.LatLng) {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ location: coordinates }, (results, status) => {
      if (status === "OK") {
        this.address = results[0].formatted_address;
        this.formattedAddress = results[0].formatted_address;
      } else {
        alert(
          "Geocode was not successful for the following reason: " +
            status +
            " for coordinates: " +
            coordinates.toString(),
        );
      }
    });
  }

  /**
   * Open the dialog to display help information.
   * @param $event: click mouse event
   * @param index: the index of the help info. See ./_constants/AppSettting for more detail.
   */
  openHelpInfo($evt: MouseEvent, index: number): void {
    this.helpInfoService.open($evt, index);
  }

  // Button 'Build Applicability' click event
  onBuildApplicability() {
    if (this.getSelectedTopicNumber() == 0) {
      this.alertifyService.confirmOk(
        "Cannot build applicability before any topics were selected.",
      );
    } else {
      this.router.navigate(["audithub/apptool/edit/" + this.csskey]);
    }
  }

  /**
   * Find the topic object by topic key.
   * @param key
   * @returns {TemplateTopic | undefined} - the topic object if found, otherwise undefined.
   */
  findTopicByKey(key: number): TemplateTopic | undefined {
    const acronyms = this.topics.flatMap((ca) => ca.acronyms);
    if (!acronyms) return undefined;
    const acronym = acronyms.find(
      (ac) => ac.topics && ac.topics.some((tp) => tp.topicKey === key),
    );
    if (!acronym) return undefined;
    const found = acronym.topics.find((tp) => tp.topicKey === key);
    return found;
  }

  // refresh the model data to reflex check/uncheck on topics.
  refreshSelectedTopicNumber(key: number, change: boolean) {
    const category = this.topics.find(
      (ca) => ca.category === this.currentCategory,
    );
    if (!category) return;

    const acronym = category.acronyms.find(
      (ac) => ac.topics && ac.topics.some((tp) => tp.topicKey === key),
    );
    if (!acronym) return;

    const topic = acronym.topics.find((tp) => tp.topicKey === key);
    if (topic) {
      //if (topic.selected === change) return; // no change, do nothing.
      topic.selected = change;
      change ? acronym.selected++ : acronym.selected--;
      change ? category.selected++ : category.selected--;
    }
  }

  // For new audit, cache the topic key for future submitting, but not call the backend yet.
  changeTopicInNewAudit(key: TemplateTopic) {
    if (key.selected) {
      if (this.currentCategory == "Custom") {
        this.cacheCustomTopic.push(key.topicKey);
      } else {
        this.cacheTopickey.push(key.topicKey);
      }
    } else {
      if (this.currentCategory == "Custom") {
        let idx = this.cacheCustomTopic.indexOf(key.topicKey);
        if (idx != -1) this.cacheCustomTopic.splice(idx, 1);
      } else {
        let idx = this.cacheTopickey.indexOf(key.topicKey);
        if (idx != -1) this.cacheTopickey.splice(idx, 1);
      }
    }
    this.refreshSelectedTopicNumber(key.topicKey, key.selected);
  }

  /**
   * Processing when a topic is checked/unchecked in an existing template.
   * @returns {number} - the number of selected topics.
   */
  changeTopicInExistingAudit(
    topic: TemplateTopic,
    title: string,
    hasMirroredTopic: boolean,
  ): void {
    var data: CheckTopic = {
      csskey: this.csskey ?? 0,
      topicKey: [topic.topicKey],
      flag: topic.selected,
      isCustomTopic: this.currentCategory === "Custom",
      isDual: hasMirroredTopic,
    };
    this.service.showLoadingAnimation(true);
    this.isLoading = true;
    this.service.updateTopic(data).subscribe(
      (val) => {
        this.refreshSelectedTopicNumber(topic.topicKey, data.flag); // refresh the model data to reflex check/uncheck on topics.
        this.service.showLoadingAnimation(false);

        if (topic.selected) {
          // Add topic to the template.
          this.alertifyService.success("Topic '" + topic.topic + "' added.");
          if (data.isCustomTopic) {
            // For custom topic, update the local storage.
            this.service.addCustomTopicLocalStorage(
              this.csskey ?? 0,
              title,
              topic.topic ?? "",
              data.topicKey[0],
            );
          } else {
            // For regular topic, remove the template from local storage becasue it is not acurate any more duo to the topic added.
            this.service.removeTemplateFromStorage(this.csskey ?? 0);
          }
        } else {
          // Remove topic from the template.
          this.alertifyService.success("Topic '" + topic.topic + "' canceled.");

          if (data.isCustomTopic) {
            // There is difference when dealing with custom topics.
            this.service.removeCustomTopicFromStorage(
              this.csskey ?? 0,
              data.topicKey[0],
            );
          } else {
            this.service.removeTopicFromStorage(
              this.csskey ?? 0,
              data.topicKey[0],
            );
          }
          // delete the template from the cache if there is no rulebook in the template.
          this.checkTempalteStorage(this.csskey ?? 0);
        }
        this.updateDualLanguageFlag(); // Update dual language flag in the session storage.
      },
      (error) => {
        console.error("updateTopic() return: " + error.error);
        this.service.showLoadingAnimation(false);
        //this.serverMessage = error.error;
      },
      () => {
        this.service.showLoadingAnimation(false);
        this.isLoading = false;
      },
    );
  }

  /**
   * Update dual language flag in the session storage.
   * This is used to update the dual language setting when a topic is checked/unchecked in an existing template.
   * If there is a dual language protocol, and the dual language checkbox is checked, then set the dual language flag to 2.
   * If there is no dual language protocol, and the dual language checkbox is checked, then set the dual language flag to 1.
   * If the dual language checkbox is unchecked, then do not update the dual language flag.
   */
  updateDualLanguageFlag() {
    if (this.csskey && this.csskey > 0 && this.dualLanguageCheckbox) {
      {
        this.service.updateTemplateDualLanguage(
          this.csskey ?? 0,
          this.hasDualLanguageProtocol() ? 2 : 1,
        );
        this.service.setDualLanguageTemplate(
          this.hasDualLanguageProtocol() ? 2 : 1,
        );
      }
    }
  }

  // Event handler for topic checkbox checking/unchecking.
  onTopicChange(
    topic: TemplateTopic,
    title: string,
    acronym: string,
    releaseNum?: number,
  ) {
    let mirroredTopic = this.findTopicByKey(
      this.getMirroredTopicKey(topic.topic ?? "", acronym, releaseNum),
    );
    if (this.csskey == 0) {
      this.changeTopicInNewAudit(topic);
      // If there is a mirrored topic, then change the mirrored topic too.
      if (mirroredTopic) {
        if (mirroredTopic.selected === topic.selected) return; // no change, do nothing.
        mirroredTopic.selected = topic.selected;
        this.changeTopicInNewAudit(mirroredTopic);
      }
    } else {
      this.changeTopicInExistingAudit(
        topic,
        title,
        mirroredTopic !== undefined,
      );
      // If there is a mirrored topic, then change the mirrored topic too.
      if (mirroredTopic) {
        if (mirroredTopic.selected === topic.selected) return; // no change, do nothing.
        mirroredTopic.selected = topic.selected;
        this.changeTopicInExistingAudit(mirroredTopic, title, true);
      }
    }
    this.isSavable = true;
  }

  /**
   * Check if the template has any rulebooks in the storage.
   * If not, then remove the template from the storage.
   * @param csskey
   * @returns
   */
  checkTempalteStorage(csskey: number) {
    var cacheTemplate = localStorage.getItem("template");
    if (cacheTemplate === null) return;

    let obj: AuditContent[] = JSON.parse(cacheTemplate);

    for (var audit of obj) {
      if (audit.csskey != csskey) continue;

      if (audit.value == null || audit.value.length === 0) {
        this.service.removeTemplateFromStorage(csskey);
      }
    }
  }

  /**
   * Remove rulebook list of the topic from template in the cache.
   * @param topicKey
   */
  removeRulebooksFromCache(topicKey: number) {
    var tp = this.service.getRulebookLocalStorage(this.csskey);
    if (tp === null) return;
  }

  /**
   * Remove template from the cache;
   * Called when there is change in the template becasue the rulebook list in the cache will not be accurate.
   * @param csskey
   */
  removeTemplateFromCache(csskey: number) {
    this.service.removeTemplateFromStorage(csskey);
  }

  // Event handler: check or uncheck all the topics under a publication
  onSelectAllClick(event) {
    event.stopPropagation(); // do not propagate the event to expand or collapse the accordion.
  }

  // Event handler: check all topic checkbox change event.
  onSelectAllChange(
    event: { checked: boolean },
    acronym: string,
    checkDual: boolean = true,
  ) {
    if (this.csskey == 0) {
      // if it is a new audit, cache the topic key for future submitting, but not call the backend yet.
      if (event.checked) {
        this.cacheAllTopics(acronym);
      } else {
        // Uncheck all topics: remove all topics from the cache.
        this.removeAllTopics(acronym);
      }
    } else {
      // if it is an existing template, then call the backend to update the topics.
      this.doUpdateTopic(acronym, event.checked);
    }
    // If dual language is selected, then select the mirrored topic too.
    let pairedAcronym = this.getDualLanguageAcronym(acronym);
    if (this.dualLanguageCheckbox && pairedAcronym !== "" && checkDual) {
      // If the paired acronym is not the same as the current acronym, then select all topics in the paired acronym.
      this.onSelectAllChange(event, pairedAcronym, false); // Set checkDual to false to avoid infinite loop.
    }
    this.isSavable = true;
  }

  cacheAllTopics(acronym) {
    // Cache all topics in the template.
    let acronymObj = this.findAcronymObj(acronym);
    if (!acronymObj) return;
    const category = this.topics.find(
      (ca) => ca.category === this.currentCategory,
    );
    if (!category) return;

    acronymObj.topics.forEach((tp) => {
      tp.selected = true;
      if (this.currentCategory === "Custom") {
        if (!this.cacheCustomTopic.includes(tp.topicKey)) {
          acronymObj.selected++;
          category.selected++;
          this.cacheCustomTopic.push(tp.topicKey);
        }
      } else {
        if (!this.cacheTopickey.includes(tp.topicKey)) {
          acronymObj.selected++;
          category.selected++;
          this.cacheTopickey.push(tp.topicKey);
        }
      }
    });
  }

  removeAllTopics(acronym: string): void {
    // Uncheck all topics: remove all topics from the cache.
    let acronymObj = this.findAcronymObj(acronym);
    if (!acronymObj) return;
    const category = this.topics.find(
      (ca) => ca.category === this.currentCategory,
    );
    if (!category) return;

    acronymObj.topics.forEach((tp) => {
      tp.selected = false;
      if (this.currentCategory === "Custom") {
        const idx = this.cacheCustomTopic.indexOf(tp.topicKey);
        if (idx !== -1) {
          acronymObj.selected--;
          category.selected--;
          this.cacheCustomTopic.splice(idx, 1);
        }
      } else {
        const idx = this.cacheTopickey.indexOf(tp.topicKey);
        if (idx !== -1) {
          acronymObj.selected--;
          category.selected--;
          this.cacheTopickey.splice(idx, 1);
        }
      }
    });
  }

  onShareChange(event) {
    this.isShared = event.checked;
    this.isSavable = true;
  }

  // User checked/unchecked checkbox 'select all' and confirmed.
  doUpdateTopic(acronym: string, action: boolean) {
    this.service.showLoadingAnimation(true);
    this.isLoading = true;
    var tpKeys = new Array();
    var isCustom = false;

    for (var ca of this.topics) {
      for (var ac of ca.acronyms) {
        if (ac.acronym == acronym) {
          if (ca.category === "Custom") {
            isCustom = true;
          }
          ac.topics.forEach((tp) => {
            if (tp.selected != action) {
              tpKeys.push(tp.topicKey);
            }
          });
          break;
        }
      }
    }

    var data: CheckTopic = {
      csskey: this.csskey,
      topicKey: tpKeys,
      flag: action,
      isCustomTopic: isCustom,
      isDual: false,
    };

    this.service.updateTopic(data).subscribe(
      (val) => {
        data.topicKey.forEach((x) => {
          this.refreshSelectedTopicNumber(x, data.flag);
          if (data.flag) {
            // Add topic to the template.
            this.service.removeTemplateFromStorage(this.csskey); // remove the tempalte from cache because it is not accurate any more.
          } else {
            // Remove topic from the template.
            if (data.isCustomTopic) {
              // There is difference when dealing with custom topics.
              this.service.removeCustomTopicFromStorage(this.csskey, x);
            } else {
              this.service.removeTopicFromStorage(this.csskey, x);
            }
          }
        });
        this.service.showLoadingAnimation(false);
        this.isLoading = false;
        this.isSavable = true;
      },
      (error) => {
        console.error("updateTopic() return: " + error.error);
        this.service.showLoadingAnimation(false);
        this.isLoading = false;
      },
      () => {
        this.service.showLoadingAnimation(false);
        this.isLoading = false;
      },
    );
  }

  // To delete the template
  onDeleteTemplate() {
    this.alertifyService.confirmOkCancel(
      "STP AuditHub",
      "Are you sure you want to delete the template?",
      () => {
        if (this.csskey == 0 || this.csskey == undefined) return;
        this.service.showLoadingAnimation(true);
        this.service.deleteTemplate(this.csskey).subscribe(
          (value) => {
            this.service.showLoadingAnimation(false);
            this.service.removeItemFromCache(this.csskey);
            this.service.removeTemplateFromStorage(this.csskey);
            this.alertifyService.success("Template deleted.");
            this.router.navigateByUrl("/audithub/apptool/list");
          },
          (error) => {
            this.service.showLoadingAnimation(false);
            this.alertifyService.error(error.message);
          },
        );
      },
      () => {
        // do nothing
      },
    );
  }

  // get acronym by topickey
  afterTopickeyClick(topickey: number, action: boolean) {
    for (var ca of this.topics) {
      for (var ac of ca.acronyms) {
        for (var tp of ac.topics) {
          if (tp.topicKey == topickey) {
            action ? ac.selected++ : ac.selected--;
            return;
          }
        }
      }
    }
  }

  openDialog(acronym: string): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      width: "450px",
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("The dialog was closed:" + result.event);

      if (result.event === "Confirm") {
        // Only delete topic when user confirms.
        this.doUpdateTopic(acronym, false);
      } else {
        this.afterTopicUpdate(acronym, true);
      }
    });
  }

  // get the number of topics select for this template
  getSelectedTopicNumber() {
    var result = 0;

    this.topics.forEach((tp) => {
      tp.acronyms.forEach((ac) => {
        result += ac.selected;
      });
    });

    return result;
  }

  // set selected topic numbers for the acronym
  afterTopicUpdate(acronym: string, action: boolean) {
    const category = this.topics.find((ca) =>
      ca.acronyms.some((ac) => ac.acronym === acronym),
    );
    if (!category) return;

    const acronymObj = category.acronyms.find((ac) => ac.acronym === acronym);
    if (!acronymObj) return;

    acronymObj.selected = 0;
    acronymObj.topics.forEach((tp) => {
      tp.selected = action;
      if (action) acronymObj.selected++;
    });
    return;
  }

  findAcronymObj(acronym: string) {
    const category = this.topics.find(
      (ca) => ca.category === this.currentCategory,
    );
    if (!category) return undefined;
    return category.acronyms.find((ac) => ac.acronym === acronym);
  }
}

@Component({
  selector: "confirm-dialog",
  templateUrl: "confirm-dialog.html",
  standalone: false,
})
export class ConfirmDialog {
  constructor(public dialogRef: MatDialogRef<ConfirmDialog>) {}

  onConfirmClick() {
    this.dialogRef.close({ event: "Confirm" });
  }

  onCancelClick(): void {
    this.dialogRef.close({ event: "Cancel" });
  }
}
